import { KeyCodeTypes } from 'common/interfaces';
import { ResizeHandlerPositions } from 'hooks/hooks.interfaces';
import { useTypedSelector } from 'hooks/useTypedSelector';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import CoordsService from 'services/CoordsService';
import {
  changeActiveBreakpointIfNeeded,
  changeArtboardProps,
  changeCanvasHeightOnValue,
  setIsBreakpointUpdateFreezing,
} from 'store/reducers/common.slice';

export default function useArtboardResizeHandler(
  ref: React.RefObject<HTMLElement>,
  pressedKeys: string[],
  handlerType: ResizeHandlerPositions,
): (event: React.MouseEvent) => void {
  const activeBreakpoint = useTypedSelector(
    (state) => state.common.activeBreakpoint,
  );
  const elems = useTypedSelector((state) => state.elems.elems);
  const guides = useTypedSelector((state) => state.guides.guides);
  const dispatch = useDispatch();

  const startResizeHandler = useCallback(
    (event: React.MouseEvent): void => {
      event.preventDefault();

      const domElem = ref.current;

      if (domElem && !pressedKeys.includes(KeyCodeTypes.SPACE)) {
        document.body.style.cursor =
          handlerType === ResizeHandlerPositions.RIGHT_SIDE
            ? 'ew-resize'
            : 'ns-resize';

        const resizeWidthHandler = (pageX: number): void => {
          const elemCoords = CoordsService.getCoords(domElem);

          const offset = pageX - elemCoords.right;

          const newWidth = Math.round(
            parseInt(domElem.style.width) + offset * 2,
          );

          if (newWidth < 300) {
            return;
          }

          domElem.style.width = newWidth + 'px';
          dispatch(changeArtboardProps({ width: newWidth }));

          if (!activeBreakpoint) return;

          dispatch(
            changeActiveBreakpointIfNeeded({
              breakpointValue: activeBreakpoint.breakpoint,
              width: newWidth,
              elems,
              guides,
            }),
          );
        };

        const resizeHeightHandler = (pageY: number): void => {
          const elemCoords = CoordsService.getCoords(domElem);

          const offset = pageY - elemCoords.bottom;

          const newHeight = parseInt(domElem.style.height) + offset;

          if (newHeight < 500) {
            return;
          }

          domElem.style.height = newHeight + 'px';

          dispatch(changeArtboardProps({ height: newHeight }));
          dispatch(changeCanvasHeightOnValue(offset));
        };

        const onMouseMove = (event: MouseEvent): void => {
          if (handlerType === ResizeHandlerPositions.RIGHT_SIDE) {
            resizeWidthHandler(event.pageX);
          } else if (handlerType === ResizeHandlerPositions.BOTTOM_SIDE) {
            resizeHeightHandler(event.pageY);
          }
        };

        document.addEventListener('mousemove', onMouseMove);

        document.onmouseup = function () {
          document.removeEventListener('mousemove', onMouseMove);

          dispatch(setIsBreakpointUpdateFreezing(false));

          document.body.style.cursor = 'inherit';
          document.onmouseup = null;
        };
      }
    },
    [dispatch, pressedKeys, ref, handlerType, activeBreakpoint, elems, guides],
  );

  return startResizeHandler;
}
